import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import {
    Alert,
    Button,
    CircularProgress,
    FormControl,
    Grid,
    ImageListItem,
    ImageListItemBar,
    InputLabel,
    MenuItem,
    Select,
    Skeleton,
    TextField,
    Typography,
} from '@mui/material';
import ArrowBackIos from '@mui/icons-material/ArrowBackIos';
import AddressInput from '../common/addressinput';
import { useTranslation } from 'react-i18next';
import Address from '../domain/address';
import Footer from '../main/footer';
import FooterPadding from '../main/footerpadding';
import { saveSurvey, deleteSurvey, getSurveyExcludedImages } from '../modules/surveydata';
import { useDispatch } from 'react-redux';
import SaveButton from '../common/savebutton';
import RichTextEditor from '../common/richtexteditor';
import DeleteIcon from '@mui/icons-material/Delete';
import ConfirmationDialog from '../common/confirmationdialog';
import ZoomableImage from './zoomableimage';
import { flatMapDeep, isObject, uniqBy, map, cloneDeep } from 'lodash';
import { useImageUrlCache } from './useImageUrlCache';
import { clearLocationFullDataById } from '../modules/locationdata';
import ImageListAccordion from './imagelistaccordion';

const styles = {
    backArrow: {
        cursor: 'pointer',
    },
    textField: {
        mx: 1,
        width: '95%',
    },
    select: {
        mt: 2,
        mx: 1,
        width: '95%',
    },
    image: {
        maxWidth: '100%',
        height: 'auto',
    },
};

export default function SurveyEditView(props) {
    const dispatch = useDispatch();
    const [survey, setSurvey] = useState();
    const [surveyEnrichment, setSurveyEnrichment] = useState();
    const [saving, setSaving] = useState(false);
    const { t } = useTranslation();
    const fetchEditorContentRef = useRef(null);
    const [confirmRemoveOpen, setConfirmRemoveOpen] = useState(false);
    const [uniqImgIds, setUniqImgIds] = useState([]);
    const [excludedImages, setExcludedImages] = useState([]);

    const onCancel = () => {
        props.history.goBack();
    };

    const getAllSurveyPictures = useCallback((survey) => {
        return flatMapDeep(survey, (value, key) => {
            if (key === 'pictures' && Array.isArray(value)) return value;
            if (key === 'coverPicture' && isObject(value)) return [value];
            if (isObject(value)) return getAllSurveyPictures(value);
            return [];
        });
    }, []);

    useEffect(() => {
        const pictures = getAllSurveyPictures(props.survey);
        setUniqImgIds(uniqBy(map(pictures, 'imageId')));
    }, [props.survey, getAllSurveyPictures]);

    const { imageUrls } = useImageUrlCache(uniqImgIds, props.survey.id, dispatch);

    const onSave = async () => {
        setSaving(true);
        const editorHtmlContent = fetchEditorContentRef.current();
        const version = parseInt(survey.version, 10);
        const surveyNew = cloneDeep(survey);
        surveyNew.version = version + 1;
        var result = await saveSurvey(surveyNew, editorHtmlContent, excludedImages)(dispatch);
        if (result) {
            setSurvey(surveyNew);
            setSurveyEnrichment(editorHtmlContent);
            // when survey is created from location, we need to clear the location data
            if (surveyNew.locationId && surveyNew.locationId > 0) {
                clearLocationFullDataById(surveyNew.locationId)(dispatch);
            }
        }
        setSaving(false);
    };

    const getEditorContentHtml = (fetchEditorContent) => {
        fetchEditorContentRef.current = fetchEditorContent;
    };

    const onAddressChange = (address) => {
        const surveyNew = cloneDeep(survey);
        surveyNew.address = address;
        setSurvey(surveyNew);
    };

    const onCommonChange = (event) => {
        const surveyNew = cloneDeep(survey);
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'comment':
                surveyNew.comment = value;
                break;
            case 'survey-usage':
                surveyNew.usage = value;
                break;
            case 'survey-classification':
                surveyNew.classification = value;
                break;
            case 'survey-usage-other':
                surveyNew.propertyUsageOther = value;
                break;
            case 'name':
                surveyNew.name = value;
                break;
            case 'propertyManagerComments':
                surveyNew.propertyManagerComments = value;
                break;
            default:
                break;
        }

        setSurvey(surveyNew);
    };

    const onMachineChange = (event, roomIndex, machineIndex) => {
        const surveyNew = cloneDeep(survey);
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'machine-room-name':
                surveyNew.machineRooms[roomIndex].name = value;
                break;
            case 'machine-name':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].name = value;
                break;
            case 'air-volume':
                const newValue = value.toString().replace(',', '.');
                if (!/^\d*[.]?\d{0,2}$/.test(newValue)) {
                    return;
                } else if (newValue >= 0) {
                    surveyNew.machineRooms[roomIndex].machines[machineIndex].airVolume = newValue;
                }
                break;
            case 'machine-service-area':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].serviceArea = value;
                break;
            case 'machine-filter-class':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filterClass = value;
                break;
            case 'filter-efficiency':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filterEfficiency = value;
                break;
            case 'machine-filter-type':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filterType = value;
                break;
            case 'machine-heat-recovery-mode':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].heatRecoveryModel = value;
                break;
            case 'seal-type':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].selectedSeal = value;
                break;
            case 'used-seals':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].numberOfSeals = value;
                break;
            case 'machine-type':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].type = value;
                break;
            case 'filter-type-other':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filterTypeOther = value;
                break;
            default:
                break;
        }

        setSurvey(surveyNew);
    };

    const onFilterChange = (event, roomIndex, machineIndex, filterIndex) => {
        const surveyNew = cloneDeep(survey);
        const target = event.target;
        const value = target.value;
        const name = target.name;

        switch (name) {
            case 'filter-height':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filters[filterIndex].dimensionHeight = value;
                break;
            case 'filter-width':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filters[filterIndex].dimensionWidth = value;
                break;
            case 'filter-maxdepth':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filters[filterIndex].maxDepth = value;
                break;
            case 'filter-count':
                surveyNew.machineRooms[roomIndex].machines[machineIndex].filters[filterIndex].count = value;
                break;
            default:
                break;
        }

        setSurvey(surveyNew);
    };

    const onImageCommentChange = (event, imageId) => {
        const surveyNew = cloneDeep(survey);
        const target = event.target;
        const value = target.value;

        // Serach recursively for the imageId and update the comment
        const updatePictureComment = (node) => {
            if (!node) return false;

            // Current node
            if (node.pictures) {
                const picture = node.pictures.find((pic) => pic.imageId === imageId);
                if (picture) {
                    // Picture element found, update the comment
                    picture.comments = value;
                    return true;
                }
            }

            // Search from machine level
            if (node.machines) {
                for (const machine of node.machines) {
                    // if the picture is found in the machine, return true
                    if (updatePictureComment(machine)) return true;
                }
            }

            if (node.machineRooms) {
                for (const room of node.machineRooms) {
                    // if the picture is found in the machine room, return true
                    if (updatePictureComment(room)) return true;
                }
            }

            // not found, return false and continue search
            return false;
        };

        updatePictureComment(surveyNew);

        setSurvey(surveyNew);
    };

    const onRemove = () => {
        setConfirmRemoveOpen(true);
    };

    const onDoRemove = async () => {
        await deleteSurvey(survey.id);
        // flush location data if survey linked to location
        if (survey.locationId && survey.locationId > 0) {
            clearLocationFullDataById(survey.locationId)(dispatch);
        }

        setConfirmRemoveOpen(false);
        props.history.push('/surveys');
    };

    const imageMarkedToPdf = (imageId) => {
        if (excludedImages.includes(imageId)) {
            return false;
        }
        //by default, if imageId is not in exclude list or list empty, return true
        return true;
    };

    const onImageMarkedToPdf = (imageId, checked) => {
        let newExcludedImages = [...excludedImages];

        if (newExcludedImages.includes(imageId)) {
            newExcludedImages = newExcludedImages.filter((id) => id !== imageId);
        } else {
            newExcludedImages.push(imageId);
        }

        setExcludedImages(newExcludedImages);
    };

    async function fetchExcludedImages(surveyId, setExcludedImagesFunc) {
        const excludedImages = await getSurveyExcludedImages(surveyId);
        setExcludedImagesFunc(excludedImages);
    }

    useEffect(() => {
        fetchExcludedImages(props.survey.id, setExcludedImages);

        let surveyMod = props.survey;
        //temppu jotta voidaan käyttää address inputtia, väliaikinen ratkaisu
        surveyMod.address = new Address(props.survey.address);
        setSurvey(surveyMod);
    }, [props.survey]);

    useEffect(() => {
        setSurveyEnrichment(props.surveyEnrichment);
    }, [props.surveyEnrichment]);

    const hasSelectedPhraseImages = useMemo(() => {
        if (!survey || !survey.machineRooms) {
            return false;
        }

        const hasPhrases = (picture) => {
            if (!picture) return false;
            const phrases = Array.isArray(picture.phrases)
                ? picture.phrases
                : Array.isArray(picture.filedPhrases)
                  ? picture.filedPhrases
                  : [];
            return phrases.length > 0;
        };

        const isSelected = (imageId) => imageId && !excludedImages.includes(imageId);

        for (const room of survey.machineRooms) {
            if (room.pictures) {
                for (const picture of room.pictures) {
                    if (isSelected(picture.imageId) && hasPhrases(picture)) {
                        return true;
                    }
                }
            }

            if (room.machines) {
                for (const machine of room.machines) {
                    if (machine.pictures) {
                        for (const picture of machine.pictures) {
                            if (isSelected(picture.imageId) && hasPhrases(picture)) {
                                return true;
                            }
                        }
                    }
                }
            }
        }

        return false;
    }, [survey, excludedImages]);

    if (!survey) {
        return <CircularProgress color={'secondary'} size={'1rem'} />;
    }

    /*if (survey.locationId !== null && survey.locationId > 0) {
        return (
            <Grid container>
                <Grid item xs={12}>
                    <h2>
                        <ArrowBackIos onClick={onCancel} sx={styles.backArrow} name="close" />
                        <span data-testid="survey-name">{survey.name}</span>
                    </h2>
                </Grid>
                <Grid item xs={12}>
                    <Alert severity="warning">{t('survey.notAllowedToEditSurvey')}</Alert>
                </Grid>
                <Footer>
                    <Grid item>
                        <Button variant="contained" color="secondary" name="btn-close" onClick={onCancel}>
                            {t('buttons.close')}
                        </Button>
                    </Grid>
                </Footer>
                <FooterPadding />
            </Grid>
        );
    }*/

    const SkeletonLoader = ({ width = 150, height = 150 }) => (
        <Skeleton variant="rectangular" width={width} height={height} animation="wave" />
    );

    return (
        <Grid container spacing={2}>
            <Grid item xs={12}>
                <h2>
                    <ArrowBackIos onClick={onCancel} sx={styles.backArrow} name="close" />
                    <span data-testid="survey-name">
                        {survey.name} - <em>{t('general.editing')}</em>
                    </span>
                </h2>
            </Grid>
            <Grid item xs={12}>
                <h2>{t('survey.coverPicture')}</h2>
                {survey.coverPicture ? (
                    <ImageListItem
                        key={survey.coverPicture.imageId}
                        sx={{
                            maxWidth: 150,
                            width: '100%',
                        }}>
                        {!imageUrls[survey.coverPicture.imageId] ? (
                            <SkeletonLoader />
                        ) : (
                            <ZoomableImage
                                thumbUrl={imageUrls[survey.coverPicture.imageId].thumbUrl}
                                fullResUrl={imageUrls[survey.coverPicture.imageId].fullResUrl}
                                thumbStyle={styles.image}
                            />
                        )}
                        <ImageListItemBar title={survey.coverPicture.comments} />
                    </ImageListItem>
                ) : (
                    <Typography sx={{ fontFamily: 'italic' }}>{t('survey.coverPictureMissing')}</Typography>
                )}
            </Grid>
            <Grid item xs={12}>
                <h4>{t('survey.propertyDetails')}</h4>
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="name"
                    data-testid="name-input"
                    margin="normal"
                    onChange={onCommonChange}
                    label={t('survey.surveyName')}
                    value={survey.name}
                    sx={styles.textField}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="comment"
                    data-testid="comment-input"
                    margin="normal"
                    onChange={onCommonChange}
                    label={t('survey.generalComment')}
                    value={survey.comment}
                    sx={styles.textField}
                />
            </Grid>
            {/*TODO: main image with comments*/}
            <Grid item xs={12}>
                <h5>{t('general.address')}</h5>
                <AddressInput
                    address={survey.address}
                    addressChanged={onAddressChange}
                    idBase={'surveyAddr-'}
                    hideName={true}
                />
            </Grid>
            {hasSelectedPhraseImages && (
                <Grid item xs={12}>
                    <Alert severity="info">{t('survey.imagesWithPhrasesSelected')}</Alert>
                </Grid>
            )}
            <Grid item xs={12}>
                <RichTextEditor
                    initialData={surveyEnrichment}
                    getEditorContentHtml={getEditorContentHtml}
                    viewOnly={false}
                />
            </Grid>
            <Grid item xs={12}>
                <TextField
                    name="propertyManagerComments"
                    data-testid="property-manager-comments-input"
                    margin="normal"
                    onChange={onCommonChange}
                    label={t('survey.propertyManagerComments')}
                    value={survey.propertyManagerComments}
                    sx={styles.textField}
                />
            </Grid>
            <Grid item xs={12}>
                <h5>{t('survey.propertyUsageAndCalssification')}</h5>
            </Grid>
            <Grid item xs={12} md={6}>
                <Grid container>
                    <Grid item xs={12}>
                        <FormControl sx={styles.select} data-testid="survey-usage-form-control">
                            <InputLabel id="survey-usage">{t('survey.usage')}</InputLabel>
                            <Select
                                required
                                labelId="survey-usage"
                                id="survey-usage-select"
                                name="survey-usage"
                                data-testid="survey-usage-select"
                                value={survey.usage}
                                onChange={onCommonChange}>
                                <MenuItem value={1}>{t('survey.usageOffice')}</MenuItem>
                                <MenuItem value={2}>{t('survey.usageProductionIndustrial')}</MenuItem>
                                <MenuItem value={3}>{t('survey.usageHousing')}</MenuItem>
                                <MenuItem value={4}>{t('survey.usageWarehouseOrLogistics')}</MenuItem>
                                <MenuItem value={5}>{t('survey.usageShopping')}</MenuItem>
                                <MenuItem value={6}>{t('survey.usageSports')}</MenuItem>
                                <MenuItem value={7}>{t('survey.usageOther')}</MenuItem>
                            </Select>
                        </FormControl>
                    </Grid>
                    {survey.usage === 7 && (
                        <Grid item xs={12}>
                            <TextField
                                name="survey-usage-other"
                                data-testid={`survey-usage-other`}
                                margin="normal"
                                onChange={onCommonChange}
                                label={t('survey.propertyUsageOther')}
                                value={survey.propertyUsageOther}
                                sx={styles.textField}
                            />
                        </Grid>
                    )}
                </Grid>
            </Grid>
            <Grid item xs={12} md={6}>
                <FormControl sx={styles.select}>
                    <InputLabel id="survey-classification">{t('survey.classification')}</InputLabel>
                    <Select
                        required
                        labelId="survey-classification"
                        name="survey-classification"
                        id="survey-classification-select"
                        data-testid="survey-classification-select"
                        value={survey.classification}
                        onChange={onCommonChange}>
                        <MenuItem value={0}>{t('survey.undefined')}</MenuItem>
                        <MenuItem value={1}>{t('survey.classificationOda1')}</MenuItem>
                        <MenuItem value={2}>{t('survey.classificationOda2')}</MenuItem>
                        <MenuItem value={3}>{t('survey.classificationOda3')}</MenuItem>
                    </Select>
                </FormControl>
            </Grid>

            <Grid item xs={12} md={6}>
                <h4>{t('survey.machineRooms', { pcs: survey.machineRooms.length })}</h4>
            </Grid>
            {survey.machineRooms.map((machineRoom, roomIndex) => (
                <Grid container key={`machineroom_${roomIndex}`}>
                    <Grid item xs={12}>
                        <h5>
                            {t('survey.machineRoom')}: {machineRoom.name}
                        </h5>
                        <TextField
                            name="machine-room-name"
                            data-testid={`machine-room-${roomIndex}-name`}
                            margin="normal"
                            onChange={(event) => onMachineChange(event, roomIndex, null)}
                            label={t('survey.machineRoom')}
                            value={machineRoom.name}
                            sx={styles.textField}
                        />
                        <br />
                        <ImageListAccordion
                            pictures={machineRoom.pictures}
                            imageUrls={imageUrls}
                            imageMarkedToPdf={imageMarkedToPdf}
                            onImageMarkedToPdf={onImageMarkedToPdf}
                            onImageCommentChange={onImageCommentChange}
                        />
                    </Grid>

                    {machineRoom.machines &&
                        machineRoom.machines.map((machine, machineIndex) => (
                            <Grid container key={`machineroom_${roomIndex}_machine_${machineIndex}`}>
                                <Grid item xs={12}>
                                    <h5>
                                        {t('survey.machine')}: {machine.name}
                                    </h5>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="machine-name"
                                        data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-name`}
                                        margin="normal"
                                        onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                        label={t('survey.machine')}
                                        value={machine.name}
                                        sx={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl sx={styles.select}>
                                        <InputLabel id="machine-type">{t('survey.machineType')}</InputLabel>
                                        <Select
                                            required
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-machine-type-select`}
                                            labelId="machine-type"
                                            id="machine-type"
                                            name="machine-type"
                                            value={machine.type}
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}>
                                            <MenuItem value={1}>{t('survey.machineTypeSupply')}</MenuItem>
                                            <MenuItem value={2}>{t('survey.machineTypeExhaust')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="air-volume"
                                        data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-airvolume`}
                                        margin="normal"
                                        onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                        label={t('location.airVolume')}
                                        value={machine.airVolume}
                                        sx={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl sx={styles.select}>
                                        <InputLabel id="machine-filter-class">{t('survey.filterClass')}</InputLabel>
                                        <Select
                                            required
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-class-select`}
                                            labelId="machine-filter-class"
                                            id="machine-filter-class"
                                            name="machine-filter-class"
                                            value={machine.filterClass}
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}>
                                            <MenuItem value={1}>{'ePM1'}</MenuItem>
                                            <MenuItem value={2}>{'ePM2,5'}</MenuItem>
                                            <MenuItem value={3}>{'ePM10'}</MenuItem>
                                            <MenuItem value={4}>{'Coarse'}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="filter-efficiency"
                                        data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-efficienty`}
                                        margin="normal"
                                        type="number"
                                        onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                        label={t('survey.filterEfficiency')}
                                        value={machine.filterEfficiency}
                                        sx={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12} md={6}>
                                    <FormControl sx={styles.select}>
                                        <InputLabel id="machine-filter-type">{t('survey.filterType')}</InputLabel>
                                        <Select
                                            required
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-type-select`}
                                            labelId="machine-filter-type"
                                            id="machine-filter-type"
                                            name="machine-filter-type"
                                            value={machine.filterType}
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}>
                                            <MenuItem value={1}>{t('survey.filterTypeBag')}</MenuItem>
                                            <MenuItem value={2}>{t('survey.filterTypeCartridge')}</MenuItem>
                                            <MenuItem value={4}>{t('survey.filterTypeCassetteFilter')}</MenuItem>
                                            <MenuItem value={3}>{t('survey.filterTypeOther')}</MenuItem>
                                        </Select>
                                    </FormControl>
                                </Grid>
                                {machine.filterType === 3 && (
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            name="filter-type-other"
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-type-other`}
                                            margin="normal"
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                            label={t('survey.filterTypeOtherLabel')}
                                            value={machine.filterTypeOther}
                                            sx={styles.textField}
                                        />
                                    </Grid>
                                )}
                                {machine.type === 2 && (
                                    <Grid item xs={12} md={6}>
                                        <FormControl sx={styles.select}>
                                            <InputLabel id="machine-heat-recovery-mode">
                                                {t('survey.heatRecoveryModel')}
                                            </InputLabel>
                                            <Select
                                                required
                                                data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-heat-recovery-select`}
                                                labelId="machine-heat-recovery-mode"
                                                id="machine-heat-recovery-mode"
                                                name="machine-heat-recovery-mode"
                                                value={machine.heatRecoveryModel}
                                                onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}>
                                                <MenuItem value={0}>{t('survey.heatRecoveryNone')}</MenuItem>
                                                <MenuItem value={1}>{t('survey.heatRecoveryRotating')}</MenuItem>
                                                <MenuItem value={2}>{t('survey.heatRecoveryCoil')}</MenuItem>
                                                <MenuItem value={3}>{t('survey.heatRecoveryCounterflow')}</MenuItem>
                                                <MenuItem value={4}>
                                                    {t('survey.heatRecoveryRotaryWithRecirculation')}
                                                </MenuItem>
                                                <MenuItem value={5}>
                                                    {t('survey.heatRecoveryCoilWithRecirculation')}
                                                </MenuItem>
                                            </Select>
                                        </FormControl>
                                    </Grid>
                                )}
                                <Grid item xs={12} md={6}>
                                    <TextField
                                        name="machine-service-area"
                                        data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-service-area`}
                                        margin="normal"
                                        onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                        label={t('location.serviceArea')}
                                        value={machine.serviceArea}
                                        sx={styles.textField}
                                    />
                                </Grid>
                                <Grid item xs={12}>
                                    <h5>{t('survey.usedFilterDimensions')}</h5>
                                </Grid>
                                <Grid
                                    item
                                    xs={12}
                                    data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filters`}>
                                    <Grid container>
                                        <Grid item xs={12}>
                                            <Grid container>
                                                <Grid item xs={3}>
                                                    {t('survey.width')}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    {t('survey.height')}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    {t('survey.maxDepth')}
                                                </Grid>
                                                <Grid item xs={3}>
                                                    {t('survey.quantity')}
                                                </Grid>
                                            </Grid>
                                        </Grid>
                                        {!machine.filters ||
                                            (machine.filters.length === 0 && (
                                                <Grid container>
                                                    <Grid item xs={12}>
                                                        <em>{t('survey.noMachines')}</em>
                                                    </Grid>
                                                </Grid>
                                            ))}
                                        <Grid container>
                                            {machine.filters &&
                                                machine.filters.map((filter, filterIndex) => (
                                                    <React.Fragment
                                                        key={`machineroom_${roomIndex}_machine_${machineIndex}_filter_${filterIndex}`}>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                name="filter-width"
                                                                data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-${filterIndex}-width`}
                                                                margin="normal"
                                                                type="number"
                                                                onChange={(event) =>
                                                                    onFilterChange(
                                                                        event,
                                                                        roomIndex,
                                                                        machineIndex,
                                                                        filterIndex
                                                                    )
                                                                }
                                                                value={filter.dimensionWidth}
                                                                sx={styles.textField}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                name="filter-height"
                                                                data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-${filterIndex}-height`}
                                                                margin="normal"
                                                                type="number"
                                                                onChange={(event) =>
                                                                    onFilterChange(
                                                                        event,
                                                                        roomIndex,
                                                                        machineIndex,
                                                                        filterIndex
                                                                    )
                                                                }
                                                                value={filter.dimensionHeight}
                                                                sx={styles.textField}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                name="filter-maxdepth"
                                                                data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-${filterIndex}-maxdepth`}
                                                                margin="normal"
                                                                type="number"
                                                                onChange={(event) =>
                                                                    onFilterChange(
                                                                        event,
                                                                        roomIndex,
                                                                        machineIndex,
                                                                        filterIndex
                                                                    )
                                                                }
                                                                value={filter.maxDepth}
                                                                sx={styles.textField}
                                                            />
                                                        </Grid>
                                                        <Grid item xs={3}>
                                                            <TextField
                                                                name="filter-count"
                                                                data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-filter-${filterIndex}-count`}
                                                                margin="normal"
                                                                type="number"
                                                                onChange={(event) =>
                                                                    onFilterChange(
                                                                        event,
                                                                        roomIndex,
                                                                        machineIndex,
                                                                        filterIndex
                                                                    )
                                                                }
                                                                value={filter.count}
                                                                sx={styles.textField}
                                                            />
                                                        </Grid>
                                                    </React.Fragment>
                                                ))}
                                        </Grid>
                                    </Grid>
                                </Grid>
                                <Grid item xs={12}>
                                    <h5>{t('survey.usedSeals')}</h5>
                                </Grid>
                                <Grid container>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            name="seal-type"
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-seal-type`}
                                            margin="normal"
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                            label={t('survey.sealType')}
                                            value={machine.selectedSeal}
                                            sx={styles.textField}
                                        />
                                    </Grid>
                                    <Grid item xs={12} md={6}>
                                        <TextField
                                            name="number-of-seals"
                                            data-testid={`machine-room-${roomIndex}-machine-${machineIndex}-number-of-seals`}
                                            margin="normal"
                                            type="number"
                                            onChange={(event) => onMachineChange(event, roomIndex, machineIndex)}
                                            label={t('survey.quantity')}
                                            value={machine.numberOfSeals}
                                            sx={styles.textField}
                                        />
                                    </Grid>
                                </Grid>

                                <Grid container>
                                    <Grid item xs={12}>
                                        <ImageListAccordion
                                            pictures={machine.pictures}
                                            imageUrls={imageUrls}
                                            imageMarkedToPdf={imageMarkedToPdf}
                                            onImageMarkedToPdf={onImageMarkedToPdf}
                                            onImageCommentChange={onImageCommentChange}
                                        />
                                    </Grid>
                                </Grid>
                            </Grid>
                        ))}

                    <Grid item xs={12}>
                        {/*<Button
                            id="btn-new-machine"
                            variant="contained"
                            color="secondary"
                            name="btn-new-machiner"
                            onClick={null}>
                            TODO: new machine
                        </Button>*/}
                    </Grid>
                </Grid>
            ))}
            <Grid item xs={12}>
                {/*<Button
                    id="btn-new-machine"
                    variant="contained"
                    color="secondary"
                    name="btn-new-machiner"
                    onClick={null}>
                    TODO: new machine room
                </Button>*/}
            </Grid>
            <Footer>
                <Grid item>
                    <Button
                        variant="contained"
                        name="poista"
                        color="secondary"
                        onClick={onRemove}
                        startIcon={<DeleteIcon />}
                        disabled={saving}>
                        {t('buttons.remove')}
                    </Button>
                </Grid>
                <Grid item>
                    <SaveButton saving={saving} disabled={saving} onSubmit={onSave} />
                </Grid>
                <Grid item>
                    <Button variant="contained" color="primary" name="sulje" onClick={onCancel} disabled={saving}>
                        <i className="fas fa-window-close"></i>&nbsp;{t('buttons.close')}
                    </Button>
                </Grid>
            </Footer>
            <FooterPadding />
            <ConfirmationDialog
                cancel={() => setConfirmRemoveOpen(false)}
                confirm={onDoRemove}
                open={confirmRemoveOpen}
                confirmText={t('buttons.remove')}
                useDelay={true}>
                {t('survey.confirmRemove')}
            </ConfirmationDialog>
        </Grid>
    );
}
